import deepEqual from 'fast-deep-equal'
import type {
  CommandArchive,
  CommandFromServer,
  CommandToSave,
  DoodleLayer,
  DoodleLayerId,
  DoodleUser,
  PdfOutputOptions,
  ShapeModel,
  Stamp,
} from '../../../server/src/models'
import { userCursors } from '../../doodle/user-cursor/UserCursors'
import { interpretSpreadsheetCommand, resetSpreadsheetData } from '../../doodle/view/Spreadsheet'
import { i18n } from '../i18n'
import { notifyError } from '../notifyError'
import { comparing, downloadFile, reverseArray } from '../utils'
import { DoodleCommentReply, DoodleDocument, DoodlePageContent } from './DoodleDocument'
import { DoodleChecklist, DoodleChecklistItem } from './DoodleShape'
import { OnDownloadProgress, doodleDocumentToImagePdf, doodlePageContentToCanvas } from './doodleDocumentToImagePdf'
import { doodleShapeFromModel } from './doodleShapeModel'

const defaultActiveLayerId = (layers: readonly DoodleLayer[]) =>
  (layers.findLast(l => !l.type && l.visible) ?? layers.findLast(l => !l.type))?.id ?? ''

export class DoodleDataContext {
  users: CommandArchive['users'] = []
  currentUser?: CommandArchive['user']
  metadata?: CommandArchive['metadata']
  stamps?: readonly Stamp[] = undefined
  activeLayerId: DoodleLayerId = '-'
  commands: (CommandToSave & { readonly shape?: ShapeModel })[] = []
  hasSpreadsheet = false
  spreadsheetUpdatedUid?: string

  constructor(
    readonly doodleId: string,
    private doc: DoodleDocument,
    private readonly onDownloadProgress?: OnDownloadProgress,
  ) {}

  withReset() {
    return new DoodleDataContext(this.doodleId, this.doc.withReset())
  }

  get timestamp() {
    const { metadata, commands } = this
    let commandTimestamp = 0
    for (let i = commands.length - 1; i >= 0; i--) {
      if (commands[i].timestamp) {
        commandTimestamp = commands[i].timestamp
        break
      }
    }
    const timestamp = Math.max(metadata?.createdAt ?? 0, metadata?.finalizedAt ?? 0, commandTimestamp)
    return timestamp
  }

  get resolutionScale() {
    return this.doc.resolutionScale
  }

  get totalPageCount() {
    return this.doc.totalPageCount
  }

  getPageContents(pageNumber: number): DoodlePageContent {
    return this.doc.getPage(pageNumber)
  }

  private getPageContentsMutable(pageNumber: number) {
    return this.doc.getPage(pageNumber)
  }

  toImagePdfFile(options?: PdfOutputOptions): Promise<Uint8Array<ArrayBuffer>> {
    return doodleDocumentToImagePdf(
      this.doodleId,
      this.doc,
      this.users,
      this.timestamp,
      this.metadata?.finalizedAt,
      options,
      this.onDownloadProgress,
    )
  }

  async downloadImage(type: 'png', pageNumber: number) {
    const metadata = this.metadata
    if (!metadata) {
      return
    }
    const canvas = await doodlePageContentToCanvas(
      this.doodleId,
      this.getPageContents(pageNumber),
      this.timestamp,
      metadata?.finalizedAt,
      1,
    )
    downloadFile(`${metadata.filename.replace(/\..+$/, '')}.${type}`, canvas.toDataURL(`image/${type}`))
  }

  interpretCommand(command: CommandFromServer): unknown {
    // 実行済みの自分のコマンドは再実行しないようにします。
    // fixme: 現状、コメントの返信に日時が入りません。
    if ((command as CommandToSave).timestamp && (command as CommandToSave).uid === this.currentUser?.uid) {
      const unconfirmedCommand = { ...command, timestamp: 0 }
      const existingCommand = reverseArray(this.commands).find(c => deepEqual(c, unconfirmedCommand))
      if (existingCommand) {
        ;(existingCommand as { timestamp: number }).timestamp = (command as CommandToSave).timestamp
        return
      }
    }

    switch (command.type) {
      case 'archive': {
        this.users = command.users
        this.currentUser = command.user
        this.metadata = command.metadata
        this.stamps = command.stamps && [...command.stamps].sort(comparing((stamp: Stamp) => stamp.name))
        const sheet = command.spreadsheets?.[0]
        this.hasSpreadsheet = !!sheet
        this.spreadsheetUpdatedUid = sheet?.updatedBy
        this.doc = this.doc.withReset()
        const isActiveLayerUnset = this.activeLayerId === '-'
        document.title = i18n.doodleDocumentTitle(this.metadata.filename)
        userCursors.clear()
        for (const user of command.users) {
          userCursors.add(user)
        }
        for (const [pageNumberString, overlays] of Object.entries(command.overlays ?? {})) {
          this.doc.getPage(+pageNumberString).overlays = overlays
        }
        for (const pastCommand of command.commands) {
          try {
            this.interpretCommand(pastCommand)
          } catch (error: unknown) {
            console.warn('archive command interpretation error', error, pastCommand)
          }
        }
        if (sheet) {
          delete (sheet as { updatedBy?: string }).updatedBy
          resetSpreadsheetData(sheet)
        }
        if (isActiveLayerUnset) {
          this.activeLayerId = defaultActiveLayerId(this.doc.getPage(1).layers)
        }
        topbar.hide()
        return true
      }
      case 'user:updated': {
        const index = this.users.findIndex(user => user.uid === command.user.uid)
        const user: DoodleUser = {
          ...this.users[index],
          ...command.user,
          permissions: { ...(this.users[index]?.deleted ? undefined : this.users[index]?.permissions), ...command.user.permissions },
        }
        if (user.uid) {
          this.users = index === -1 ? [...this.users, user] : [...this.users.slice(0, index), user, ...this.users.slice(index + 1)]
          if (user.uid === this.currentUser?.uid) {
            this.currentUser = { ...this.currentUser, ...user }
          } else {
            userCursors.show(user)
          }
        }
        return true
      }
      case 'user:cursor':
        userCursors.pushPoints(command.uid, command.page, command.points)
        return true

      case 'page:rotated': {
        this.commands.push(command)
        const contents = this.getPageContentsMutable(command.page)
        const diffRotation = command.rotation - contents.backgroundRotation
        if (diffRotation) {
          const matrix = new DOMMatrix()
            .translateSelf(contents.height / 2, contents.width / 2)
            .rotateSelf(0, 0, diffRotation)
            .translateSelf(-contents.width / 2, -contents.height / 2)
          const abcdef = ['a', 'b', 'c', 'd', 'e', 'f'] as const
          contents.shapes.updateShapes(shape => {
            const transform = matrix.multiply(shape.transformMatrix)
            for (const a of abcdef) {
              Math.abs(transform[a]) < 1e-10 && (transform[a] = 0)
            }
            return shape.with({ transform: String(transform) })
          })
          contents.backgroundRotation = command.rotation
        }
        return true
      }
      case 'shape:created':
      case 'shape:updated': {
        this.commands.push(command)
        const user = this.users.find(user => user.uid === command.uid)
        const shapes = this.getPageContentsMutable(command.shape.page).shapes
        let shape = doodleShapeFromModel(
          command.shape,
          user?.name || user?.email || '',
          user?.color || '#bbb',
          command.uid === this.currentUser?.uid,
        )
        // チェックリストの項目は増減だけ反映してチェック状態の変更は無視します。
        // （チェック状態は check:changed コマンドでのみ変更します。現在のチェック状態を直近の check:changed だけで判別できるようにするためです。）
        if (command.shape.type === 'checklist') {
          const oldShape = shapes.findShape(command.shape.id) as DoodleChecklist | undefined
          const items = Object.fromEntries(
            Object.entries(command.shape.items)
              .sort((a, b) => a[1].order - b[1].order)
              .map<[string, DoodleChecklistItem]>(([itemId, item]) => {
                const oldItem = oldShape?.properties.items[itemId]
                return [itemId, { ...item, checked: oldItem?.checked, disabled: oldItem?.disabled }]
              }),
          )
          shape = shape.with({ items })
        }
        command.type === 'shape:created' ? shapes.addShape(shape) : shapes.updateShape(shape)
        return true
      }
      case 'shape:deleted': {
        {
          const shape = this.commands.find(({ shape }) => shape && shape.id === command.shape.id)?.shape
          shape && this.commands.push({ ...command, shape })
        }
        this.getPageContentsMutable(command.shape.page).shapes.deleteShapeById(command.shape.id)
        return true
      }
      case 'check:changed': {
        const shapeId = command.shape.id
        {
          const shape = this.commands.find(({ shape }) => shape && shape.id === shapeId)?.shape
          shape && this.commands.push({ ...command, shape })
        }
        const shapes = this.getPageContentsMutable(command.shape.page).shapes
        const shape = shapes.findShape(shapeId)
        if (shape?.type !== 'checklist') {
          return
        }
        const items = shape.properties.items
        const item = items[command.itemId]
        if (!item) {
          return
        }
        const user = this.users.find(user => user.uid === command.uid)
        const checked: (typeof items)[string]['checked'] = command.checked
          ? { color: user?.color, timestamp: command.timestamp, userDisplayName: user?.name || user?.email || command.uid }
          : undefined
        const disabled = !!command.checked && command.uid !== this.currentUser?.uid
        shapes.updateShape(shape.with({ items: { ...items, [command.itemId]: { ...item, checked, disabled } } }))
        return true
      }
      case 'comment:created': {
        {
          const shape = this.commands.find(({ shape }) => shape && shape.id === command.shape.id)?.shape
          shape && this.commands.push({ ...command, shape })
        }
        this.getPageContentsMutable(command.shape.page).comments.set(command.comment.id, {
          id: command.comment.id,
          shapeId: command.shape.id,
          uid: command.uid,
          comment: command.comment.content,
          replies: new Map<string, DoodleCommentReply>(),
          get createdAt() {
            return (command as { readonly timestamp: number }).timestamp
          },
        })
        return true
      }
      case 'comment:replied': {
        this.commands.push(command)
        const targetCreatedCommand = this.commands.find(c => c.type === 'comment:created' && c.comment.id === command.targetCommentId)
        const page = targetCreatedCommand?.shape?.page
        if (!page) {
          throw Error()
        }
        this.getPageContentsMutable(page)
          .comments.get(command.targetCommentId)
          ?.replies.set(command.comment.id, {
            uid: command.uid,
            comment: command.comment.content,
            get createdAt() {
              return (command as { readonly timestamp: number }).timestamp
            },
          })
        return true
      }
      case 'comment:deleted': {
        this.commands.push(command)
        const targetCreatedCommand = this.commands.find(c => c.type === 'comment:created' && c.comment.id === command.comment.id)
        const page = targetCreatedCommand?.shape?.page
        if (!page) {
          throw Error()
        }
        this.getPageContentsMutable(page).comments.delete(command.comment.id)
        return true
      }
      case 'layer:created': {
        this.commands.push(command)
        const contents = this.getPageContentsMutable(command.page)
        contents.layers = [...contents.layers, command.layer]
        return true
      }
      case 'layer:updated': {
        this.commands.push(command)
        const contents = this.getPageContentsMutable(command.page)
        contents.layers = contents.layers.map(layer => (layer.id === command.layer.id ? command.layer : layer))
        return true
      }
      case 'layer:deleted': {
        this.commands.push(command)
        const contents = this.getPageContentsMutable(command.page)
        contents.layers = contents.layers.filter(layer => layer.id !== command.layerId)
        if (this.activeLayerId === command.layerId) {
          this.activeLayerId = defaultActiveLayerId(contents.layers)
        }
        return true
      }
      case 'layer:reordered': {
        this.commands.push(command)
        const contents = this.getPageContentsMutable(command.page)
        const orderMap = new Map(command.layerIds.includes('drawing') ? undefined : [['drawing', -1]])
        command.layerIds.forEach((id, i) => orderMap.set(id, i))
        contents.layers = contents.layers.toSorted((a, b) => (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity))
        return true
      }
      case 'filename':
        this.metadata && (this.metadata = { ...this.metadata, filename: command.filename })
        document.title = i18n.doodleDocumentTitle(command.filename)
        return true
      case 'finalize':
        this.metadata && (this.metadata = { ...this.metadata, finalizedAt: command.timestamp })
        return true
      case 'output':
        switch (command.output) {
          case 'print':
            print()
            return
          case 'pdf':
            if (!this.metadata) {
              return
            }
            return (async () => {
              const url = URL.createObjectURL(new Blob([await this.toImagePdfFile(command)], { type: 'application/pdf' }))
              downloadFile(`${this.metadata!.filename.replace(/\..+$/, '')}.pdf`, url)
              URL.revokeObjectURL(url)
            })()
          case 'png':
            command.page && void this.downloadImage('png', command.page)
            return
        }
        return
      case 'spreadsheet':
        this.hasSpreadsheet = true
        this.spreadsheetUpdatedUid = command.uid
        if (command.diff) {
          delete (command.diff as { updatedBy?: unknown }).updatedBy
          interpretSpreadsheetCommand(command.diff)
        }
        return true
      case 'overlays:updated':
        this.doc.getPage(command.page).overlays = command.overlays
        return
      case 'error':
        if (command.status === 401) {
          alert(i18n.authErrorOccurred)
          location.reload()
        } else if (command.status === 403) {
          alert(i18n.permissionDenied)
        } else if (command.status === 404) {
          if (command.reload) {
            alert(i18n.reloadingSinceFileNotFound)
            location.reload()
          } else {
            alert(i18n.notFound)
          }
        } else {
          console.warn('Received error command', command)
          notifyError(i18n.errorOccurred)
        }
        return
      default:
        console.warn('Received Unknown Command', command)
        return
    }
  }
}
